// libraries
import React, { useState, useEffect } from 'react'

// components
import Section from '/react4xp/components/Section'
import Form from '/react4xp/components/Form/Form'
import Checkbox from '/react4xp/components/Form/Checkbox'
import Input from '/react4xp/components/Form/Input'
import Select from '/react4xp/components/Form/Select'
import TextArea from '/react4xp/components/Form/TextArea'
import Submit from '/react4xp/components/Form/Submit'
import Upload from '/react4xp/components/Form/Upload'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'
import ReCaptcha from '../Recaptcha'
import { recaptchaCallbackFunction } from '/lib/reactutils/recaptcha'

export interface SpeakUpFormProps {
	topInfos: {
		title: string
		intro: string
	}
	form: {
		endpoint: string
		onSuccess: () => void
		onError: () => void
	}
	steps: {
		step1: {
			title: string
			inputs: [
				{
					id: string
					name: string
					label: string
					defaultValue: string
					options: Array<{
						label: string
						value: string
					}>
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
					}
				},
				{
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				},
				{
					id: string
					name: string
					label: string
					defaultValue: string
					options: Array<{
						label: string
						value: string
					}>
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
					}
				},
				{
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				},
				{
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				},
				{
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				},
				{
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				},
				{
					id: string
					name: string
					label: string
					defaultValue: string
					options: Array<{
						label: string
						value: string
					}>
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
					}
				},
			]
		},
		step2: {
			title: string
			inputs: Array<{
				type: 'input' | 'select' | 'textarea' | 'upload'
				input?: {
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				}
				select?: {
					id: string
					name: string
					label: string
					defaultValue: string
					options: Array<{
						label: string
						value: string
					}>
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
					}
				}
				textarea?: {
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					placeholder?: string
					errorMessages?: {
						required: string
					}
				}
				upload?: {
					id: string
					name: string
					label: string
					required?: boolean
					errorMessages?: {
						required?: string
						maxFileSize?: string
						format?: string
					}
					maxFileSize?: number
					uploadButtonText?: string
					accept?: string
					multiple?: boolean
					helperText?: string
				}
			}>
		}
	}
	privacyCheckbox: {
		name: string
		required: boolean
		errorMessages: {
			required: string
		}
		text: string
	}
	submit: {
		label: string
		successMessage: string
		validationMessage?: {
			one?: string
			many?: string
		}
	}
	siteKey?: string
	validationRecaptchaUrl?: string
}

export default function SpeakUpForm({
	topInfos,
	form,
	steps,
	privacyCheckbox,
	submit,
	siteKey,
	validationRecaptchaUrl
}: SpeakUpFormProps) {
	const [verified, setVerified] = useState(false)

	// hide / show the other field
	const [otherField, setOtherField] = useState(false)

	// hide / show the anonymous field
	const [anonymousField, setAnonymousField] = useState(false)

	// reset forms
	useEffect(() => {
        const handleFormReset = () => {
            //setVerified(false)
            setOtherField(false)
            setAnonymousField(false)
        }

        document.addEventListener('resetForm', handleFormReset)
        
        return () => {
            document.removeEventListener('resetForm', handleFormReset)
        }
    }, [])

	return (
		<div className='page--forms'>

			<Section textColor='text-dark' className='top-section' bgColor='bg-beige-050'>
				<div className='grid-container'>
					<div className='grid-row'>
						<div className='c-12 c-md-10 c-lg-8'>

							{isNonWhitespaceString(topInfos?.title) && (
								<h1 className='title-lg top-section__title'>
									{topInfos?.title}
								</h1>
							)}

							{isNonWhitespaceString(topInfos?.intro) && (
								<p
									dangerouslySetInnerHTML={{ __html: topInfos?.intro }}
									suppressHydrationWarning
									className='top-section__intro'
								/>
							)}

						</div>
					</div>
				</div>
			</Section>

			<Section textColor='text-dark' className='form-section' bgColor='bg-beige-050'>
				<div className='grid-container'>
					<Form
						endpoint={form?.endpoint}
						clearOnSuccess
						isFormData
						onSuccess={form?.onSuccess}
						onError={form?.onError}
					>

						<Input type="text" hidden value="speak_up_form" name="form_type" id="form_type" />

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>1.</span> {steps?.step1?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--space-left'>
							<div className='grid-row'>
								
								<div className='c-md-1 c-lg-2'></div>
								<div className='c-md-1 border'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>

									<Select
										id={steps?.step1?.inputs[0].id}
										name={steps?.step1?.inputs[0].name}
										label={steps?.step1?.inputs[0].label}
										defaultValue={steps?.step1?.inputs[0].defaultValue}
										helperText={steps?.step1?.inputs[0].helperText}
										options={steps?.step1?.inputs[0].options}
										required={steps?.step1?.inputs[0].required}
										errorMessages={steps?.step1?.inputs[0].errorMessages}
										onOptionSelect={(value) => {
											setOtherField(value === 'Other')
										}}
									/>

									{otherField && (
										<Input
											type={steps?.step1?.inputs[1].type}
											id={steps?.step1?.inputs[1].id}
											name={steps?.step1?.inputs[1].name}
											label={steps?.step1?.inputs[1].label}
											helperText={steps?.step1?.inputs[1].helperText}
											required={steps?.step1?.inputs[1].required}
											errorMessages={steps?.step1?.inputs[1].errorMessages}
										/>
									)}

									<Select
										id={steps?.step1?.inputs[2].id}
										name={steps?.step1?.inputs[2].name}
										label={steps?.step1?.inputs[2].label}
										defaultValue={steps?.step1?.inputs[2].defaultValue}
										helperText={steps?.step1?.inputs[2].helperText}
										options={steps?.step1?.inputs[2].options}
										required={steps?.step1?.inputs[2].required}
										errorMessages={steps?.step1?.inputs[2].errorMessages}
										onOptionSelect={(value) => {
											setAnonymousField(value === 'No')
										}}
									/>

									{anonymousField && (
										<>
											<Input
												type={steps?.step1?.inputs[3].type}
												id={steps?.step1?.inputs[3].id}
												name={steps?.step1?.inputs[3].name}
												label={steps?.step1?.inputs[3].label}
												helperText={steps?.step1?.inputs[3].helperText}
												required={steps?.step1?.inputs[3].required}
												errorMessages={steps?.step1?.inputs[3].errorMessages}
											/>

											<Input
												type={steps?.step1?.inputs[4].type}
												id={steps?.step1?.inputs[4].id}
												name={steps?.step1?.inputs[4].name}
												label={steps?.step1?.inputs[4].label}
												helperText={steps?.step1?.inputs[4].helperText}
												required={steps?.step1?.inputs[4].required}
												errorMessages={steps?.step1?.inputs[4].errorMessages}
											/>

											<Input
												type={steps?.step1?.inputs[5].type}
												id={steps?.step1?.inputs[5].id}
												name={steps?.step1?.inputs[5].name}
												label={steps?.step1?.inputs[5].label}
												helperText={steps?.step1?.inputs[5].helperText}
												required={steps?.step1?.inputs[5].required}
												errorMessages={steps?.step1?.inputs[5].errorMessages}
											/>

											<Input
												type={steps?.step1?.inputs[6].type}
												id={steps?.step1?.inputs[6].id}
												name={steps?.step1?.inputs[6].name}
												label={steps?.step1?.inputs[6].label}
												helperText={steps?.step1?.inputs[6].helperText}
												required={steps?.step1?.inputs[6].required}
												errorMessages={steps?.step1?.inputs[6].errorMessages}
											/>
										</>
									)}

									<Select
										id={steps?.step1?.inputs[7].id}
										name={steps?.step1?.inputs[7].name}
										label={steps?.step1?.inputs[7].label}
										defaultValue={steps?.step1?.inputs[7].defaultValue}
										helperText={steps?.step1?.inputs[7].helperText}
										options={steps?.step1?.inputs[7].options}
										required={steps?.step1?.inputs[7].required}
										errorMessages={steps?.step1?.inputs[7].errorMessages}
									/>

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>2.</span> {steps?.step2?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--space-left'>
							<div className='grid-row'>
								
								<div className='c-md-1 c-lg-2'></div>
								<div className='c-md-1 border'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>
									{steps?.step2?.inputs.map((item, i) => {
										switch (item.type) {
											case 'input':
												return (
													<Input
														key={i}
														type={item.input.type}
														id={item.input.id}
														name={item.input.name}
														label={item.input.label}
														helperText={item.input.helperText}
														required={item.input.required}
														errorMessages={item.input.errorMessages}
													/>
												)
											case 'select':
												return (
													<Select
														key={i}
														id={item.select.id}
														name={item.select.name}
														label={item.select.label}
														defaultValue={item.select.defaultValue}
														options={item.select.options}
														required={item.select.required}
														errorMessages={item.select.errorMessages}
														helperText={item.select.helperText}
													/>
												)
											case 'textarea':
												return (
													<TextArea
														key={i}
														id={item.textarea.id}
														name={item.textarea.name}
														label={item.textarea.label}
														placeholder={item.textarea.placeholder}
														required={item.textarea.required}
														errorMessages={item.textarea.errorMessages}
														helperText={item.textarea.helperText}
													/>
												)
											case 'upload':
												return (
													<Upload
														key={i}
														id={item.upload.id}
														name={item.upload.name}
														label={item.upload.label}
														required={item.upload.required}
														errorMessages={item.upload.errorMessages}
														maxFileSize={item.upload.maxFileSize}
														uploadButtonText={item.upload.uploadButtonText}
														accept={item.upload.accept}
														multiple={item.upload.multiple}
														helperText={item.upload.helperText}
													/>
												)
											default:
												return null
										}	
									})}
								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

						<div className='form-section__last-block'>
							<div className='grid-row'>
								
								<div className='c-md-2 c-lg-3'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>

									{siteKey ? <div className='recaptcha'>
										<ReCaptcha 
											siteKey={siteKey} 
											callback={(token) => recaptchaCallbackFunction(token, setVerified, validationRecaptchaUrl)}
											setVerified={setVerified}
										/>
									</div> : <></>}

									{privacyCheckbox && (
										<Checkbox
											name={privacyCheckbox?.name}
											required={privacyCheckbox?.required}
											errorMessages={privacyCheckbox?.errorMessages}
										>
											{privacyCheckbox?.text}
										</Checkbox>	
									)}

									<Submit
										disabled={siteKey && !verified}
										label={submit?.label}
										successMessage={submit?.successMessage}
										validationMessage={submit?.validationMessage}
									/>

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

					</Form>
				</div>
			</Section>

		</div>
	)
}